import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Collapse } from "@mui/material";
import { Icon } from "@iconify/react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";

const DYNAMIC_IMPORT_RELOAD_KEY = "vite-dynamic-import-reload";
const DYNAMIC_IMPORT_MAX_RELOADS = 3;
const DYNAMIC_IMPORT_RELOAD_DELAY_MS = 800;

function isStaleDynamicImportError(error) {
  const message = String(error?.message ?? "");
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("error loading dynamically imported module")
  );
}

function readDynamicImportReloadCount() {
  const raw = sessionStorage.getItem(DYNAMIC_IMPORT_RELOAD_KEY);
  const count = Number.parseInt(raw ?? "0", 10);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

/**
 * Fallback UI shown when an error boundary catches a render error.
 * Card-style layout with clear actions and optional technical details.
 */
function ErrorBoundaryFallback({ error, resetErrorBoundary }) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  // Vite returns 504 "Outdated Optimize Dep" while rebundling; lazy routes then
  // fail. Retry a few times with a short delay so prebundle can finish.
  useEffect(() => {
    if (!isStaleDynamicImportError(error)) return;
    const attempts = readDynamicImportReloadCount();
    if (attempts >= DYNAMIC_IMPORT_MAX_RELOADS) {
      sessionStorage.removeItem(DYNAMIC_IMPORT_RELOAD_KEY);
      return;
    }
    sessionStorage.setItem(DYNAMIC_IMPORT_RELOAD_KEY, String(attempts + 1));
    const timer = window.setTimeout(() => {
      window.location.reload();
    }, DYNAMIC_IMPORT_RELOAD_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [error]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const isDev = Boolean(import.meta.env?.DEV);
  const hasErrorMessage = isDev && error?.message;

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 2,
        bgcolor: "var(--portal-theme-primary)",
      }}
    >
      <Box
        sx={{
          maxWidth: 440,
          width: "100%",
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "var(--theme-ui-surface-deep)"
              : "var(--portal-theme-secondary)",
          borderRadius: 3,
          boxShadow: "0 24px 48px var(--shadow-color)",
          overflow: "hidden",
          border: "1px solid var(--divider-color)",
        }}
      >
        <Box sx={{ pt: 4, px: 3, pb: 1, textAlign: "center" }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "error.main",
              color: "error.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <Icon icon="mdi:alert-circle-outline" width={36} height={36} />
          </Box>
          <Typography variant="h6" component="h1" fontWeight={600} gutterBottom>
            {t("errors.generic")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 360, mx: "auto", lineHeight: 1.5, mb: 3 }}
          >
            {t("errorBoundary.subtitle")}
          </Typography>
        </Box>

        {hasErrorMessage && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Button
              size="small"
              onClick={() => setShowDetails((v) => !v)}
              endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ color: "text.secondary", textTransform: "none" }}
            >
              {showDetails
                ? t("errorBoundary.hideTechnicalDetails")
                : t("errorBoundary.showTechnicalDetails")}
            </Button>
            <Collapse in={showDetails}>
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  display: "block",
                  p: 2,
                  mb: 2,
                  overflow: "auto",
                  maxHeight: 140,
                  bgcolor: "action.hover",
                  borderRadius: 1.5,
                  textAlign: "start",
                  fontFamily: "monospace",
                  direction: "ltr",
                  unicodeBidi: "embed",
                  fontSize: "0.75rem",
                }}
              >
                {error.message}
              </Typography>
            </Collapse>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            justifyContent: "center",
            p: 3,
            pt: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={resetErrorBoundary}
            sx={{ minWidth: 120, textTransform: "none", fontWeight: 600 }}
          >
            {t("errorBoundary.tryAgain")}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleRefresh}
            sx={{ minWidth: 120, textTransform: "none", fontWeight: 600 }}
          >
            {t("errorBoundary.refreshPage")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default ErrorBoundaryFallback;
